(() => {
  'use strict';

  const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  let requestInFlight = false;
  let lastFocused = null;

  const route = (path) => `${root}${path.startsWith('/') ? path : `/${path}`}` || '/';
  const shell = () => document.querySelector('[data-cart-drawer-shell]');
  const panel = () => document.querySelector('[data-cart-drawer]');
  const normalizeId = (value) => Number(String(value || '').replace(/\D+/g, ''));
  const cleanTitle = (value) => String(value || '').split(' - ')[0].split(' – ')[0].trim();

  function readCommerceData() {
    const node = document.getElementById('lcdd-commerce-data');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('[LCDD] Données commerce illisibles.', error);
      return {};
    }
  }

  const commerceData = readCommerceData();
  const bundles = Array.isArray(commerceData.bundles)
    ? commerceData.bundles.map((bundle) => ({
        ...bundle,
        id: normalizeId(bundle.id),
        variantId: normalizeId(bundle.variantId),
        price: Number(bundle.price || 0),
        components: Array.isArray(bundle.components)
          ? bundle.components.map((component) => ({ ...component, id: normalizeId(component.id) }))
          : []
      }))
    : [];

  bundles.forEach((bundle) => {
    bundle.componentIds = new Set(bundle.components.map((component) => component.id));
  });

  const bundleByVariant = new Map(bundles.map((bundle) => [bundle.variantId, bundle]));
  const bundleByProduct = new Map(bundles.map((bundle) => [bundle.id, bundle]));
  const bundleProductIds = new Set(bundleByProduct.keys());
  const ownedProductIds = new Set((commerceData.ownedProductIds || []).map(normalizeId).filter(Boolean));
  const ownedHandles = new Set((commerceData.ownedHandles || []).filter(Boolean));
  const productIdByHandle = new Map();
  const componentBundlesById = new Map();
  const componentBundlesByHandle = new Map();

  bundles.forEach((bundle) => {
    productIdByHandle.set(bundle.handle, bundle.id);
    bundle.components.forEach((component) => {
      productIdByHandle.set(component.handle, component.id);
      const byId = componentBundlesById.get(component.id) || [];
      byId.push(bundle);
      componentBundlesById.set(component.id, byId);
      const byHandle = componentBundlesByHandle.get(component.handle) || [];
      byHandle.push(bundle);
      componentBundlesByHandle.set(component.handle, byHandle);
    });
  });

  /* Commandes contenant chaque création possédée : jeton FileFlare + repère de
     commande. Une possession directe (direct: true) permet le téléchargement
     immédiat ; via Bundle (direct: false) on renvoie vers Mon inventaire. */
  const ownedOrderByProduct = new Map();
  const ownedOrderByHandle = new Map();
  (Array.isArray(commerceData.ownedOrders) ? commerceData.ownedOrders : []).forEach((entry) => {
    if (!entry || typeof entry !== 'object' || !entry.token) return;
    const record = {
      productId: normalizeId(entry.productId),
      handle: entry.handle || '',
      title: String(entry.title || ''),
      token: String(entry.token),
      orderId: normalizeId(entry.orderId) || null,
      direct: entry.direct === true
    };
    const better = (existing) => !existing || (record.direct && !existing.direct);
    if (record.productId && better(ownedOrderByProduct.get(record.productId))) {
      ownedOrderByProduct.set(record.productId, record);
    }
    if (record.handle && better(ownedOrderByHandle.get(record.handle))) {
      ownedOrderByHandle.set(record.handle, record);
    }
  });

  function ownedOrderFor(productId, handle) {
    return ownedOrderByProduct.get(normalizeId(productId)) || ownedOrderByHandle.get(handle) || null;
  }

  function money(cents) {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: commerceData.currency || 'EUR',
        maximumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  function handleFromUrl(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      const match = String(value).match(/\/products\/([^/?#]+)/);
      return match ? match[1] : '';
    }
  }

  function isOwned(productId, handle) {
    return ownedProductIds.has(normalizeId(productId)) || ownedHandles.has(handle);
  }

  function membershipFor(productId, handle) {
    return componentBundlesById.get(normalizeId(productId))
      || componentBundlesByHandle.get(handle)
      || [];
  }

  function announce(message) {
    let live = document.querySelector('[data-cart-live-status]');
    if (!live) {
      live = document.createElement('div');
      live.className = 'visually-hidden';
      live.dataset.cartLiveStatus = '';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    window.setTimeout(() => { live.textContent = message; }, 20);
  }

  function showToast(message, tone = 'success') {
    let toast = document.querySelector('[data-commerce-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'commerce-toast';
      toast.dataset.commerceToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.classList.remove('is-visible', 'commerce-toast--warning');
    toast.textContent = message;
    if (tone === 'warning') toast.classList.add('commerce-toast--warning');
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => toast.classList.remove('is-visible'), 5600);
  }

  function openDrawer() {
    const currentShell = shell();
    const currentPanel = panel();
    const backdrop = document.querySelector('[data-cart-drawer-backdrop]');
    if (!currentShell || !currentPanel || !backdrop) return;

    lastFocused = document.activeElement;
    currentPanel.hidden = false;
    backdrop.hidden = false;
    currentPanel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    requestAnimationFrame(() => currentShell.classList.add('is-open'));
    currentPanel.querySelector('[data-cart-drawer-close]')?.focus({ preventScroll: true });
  }

  function closeDrawer() {
    const currentShell = shell();
    const currentPanel = panel();
    const backdrop = document.querySelector('[data-cart-drawer-backdrop]');
    if (!currentShell || !currentPanel || !backdrop) return;

    currentShell.classList.remove('is-open');
    currentPanel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    window.setTimeout(() => {
      currentPanel.hidden = true;
      backdrop.hidden = true;
    }, 220);
    lastFocused?.focus?.({ preventScroll: true });
  }

  async function fetchCart() {
    const response = await fetch(route('/cart.js'), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Le panier ne peut pas être actualisé.');
    return response.json();
  }

  function isBundleLine(item) {
    return bundleProductIds.has(normalizeId(item.product_id))
      || (Array.isArray(item.item_components) && item.item_components.length > 0);
  }

  function updateCartUi(cart) {
    document.querySelectorAll('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.classList.toggle('is-empty', cart.item_count === 0);
      count.hidden = cart.item_count === 0;
    });

    const variantIds = new Set(cart.items.map((item) => normalizeId(item.variant_id)));
    document.querySelectorAll('[data-variant-id]').forEach((button) => {
      if ('ownedDownload' in button.dataset) return;
      const inCart = variantIds.has(normalizeId(button.dataset.variantId));
      button.classList.toggle('is-in-cart', inCart);
      const label = button.querySelector('[data-atc-label]');
      if (label) {
        label.textContent = inCart
          ? (button.dataset.labelDone || 'Dans le panier')
          : (button.dataset.labelIdle || label.textContent);
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  }

  function sectionInnerHtml(html, sectionId) {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const renderedWrapper = documentFragment.getElementById(`shopify-section-${sectionId}`);
    return renderedWrapper ? renderedWrapper.innerHTML : html;
  }

  async function refreshStaticSection(sectionId) {
    const wrapper = document.getElementById(`shopify-section-${sectionId}`);
    if (!wrapper) return;
    const response = await fetch(`${route('/')}?section_id=${encodeURIComponent(sectionId)}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('Le panier ne peut pas être affiché.');
    const html = await response.text();
    wrapper.innerHTML = sectionInnerHtml(html, sectionId);
  }

  async function refreshTemplateSection(selector) {
    const content = document.querySelector(selector);
    const wrapper = content?.closest('[id^="shopify-section-"]');
    if (!wrapper) return;
    const sectionId = wrapper.id.replace('shopify-section-', '');
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('La page panier ne peut pas être actualisée.');
    const html = await response.text();
    wrapper.innerHTML = sectionInnerHtml(html, sectionId);
  }

  async function refreshCartSurfaces({ keepOpen = false } = {}) {
    const shouldOpen = keepOpen || shell()?.classList.contains('is-open');
    const cart = await fetchCart();
    updateCartUi(cart);

    const tasks = [refreshStaticSection('cart-drawer')];
    if (document.querySelector('#main-cart')) tasks.push(refreshTemplateSection('#main-cart'));
    if (document.querySelector('[data-cart-nudges]')) tasks.push(refreshTemplateSection('[data-cart-nudges]'));
    await Promise.all(tasks);

    updateCartUi(cart);
    decorateCommerce(cart);
    if (shouldOpen) openDrawer();
    return cart;
  }

  function setBusy(element, busy) {
    if (!element) return;
    element.toggleAttribute('disabled', busy);
    element.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  async function updateLines(updates) {
    const response = await fetch(route('/cart/update.js'), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ updates })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || 'Le panier ne peut pas être optimisé.');
    return payload;
  }

  async function removeCoveredUnits(cart, selectedBundles = null) {
    const activeBundles = selectedBundles || cart.items
      .map((item) => bundleByProduct.get(normalizeId(item.product_id)))
      .filter(Boolean);

    if (!activeBundles.length) return { cart, removed: [] };

    const coveredIds = new Set();
    activeBundles.forEach((bundle) => {
      bundle.componentIds.forEach((id) => coveredIds.add(id));
    });

    const updates = {};
    const removed = [];
    cart.items.forEach((item) => {
      const productId = normalizeId(item.product_id);
      if (isBundleLine(item)) return;
      if (!coveredIds.has(productId)) return;
      updates[item.key] = 0;
      removed.push({
        key: item.key,
        productId,
        title: cleanTitle(item.product_title || item.title),
        quantity: Number(item.quantity || 1)
      });
    });

    if (!removed.length) return { cart, removed };
    const updatedCart = await updateLines(updates);
    return { cart: updatedCart, removed };
  }

  async function addVariant(variantId) {
    const formData = new FormData();
    formData.set('id', String(variantId));
    formData.set('quantity', '1');
    const response = await fetch(route('/cart/add.js'), {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.description || 'Cette création ne peut pas être ajoutée.');
    return payload;
  }

  function bundleAdjustmentMessage(bundle, removed) {
    const count = removed.reduce((total, item) => total + item.quantity, 0);
    if (!count) return `${cleanTitle(bundle.title)} ajouté au panier.`;
    const noun = count > 1 ? 'créations unitaires déjà incluses ont été retirées' : 'création unitaire déjà incluse a été retirée';
    return `${cleanTitle(bundle.title)} ajouté. ${count} ${noun} du panier pour éviter les doublons.`;
  }

  async function addToCart(form, submitter) {
    if (requestInFlight) return;
    requestInFlight = true;
    setBusy(submitter, true);

    try {
      const formData = new FormData(form);
      const variantId = normalizeId(formData.get('id'));
      formData.set('quantity', '1');

      const currentCart = await fetchCart();
      if (currentCart.items.some((item) => normalizeId(item.variant_id) === variantId)) {
        updateCartUi(currentCart);
        await refreshStaticSection('cart-drawer');
        decorateCommerce(currentCart);
        openDrawer();
        announce('Cette création est déjà dans ton panier.');
        showToast('Cette création est déjà dans ton panier.', 'warning');
        return;
      }

      const response = await fetch(route('/cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || 'Cette création ne peut pas être ajoutée.');

      const addedBundle = bundleByVariant.get(variantId);
      let removed = [];
      if (addedBundle) {
        const cartAfterAdd = await fetchCart();
        const adjusted = await removeCoveredUnits(cartAfterAdd);
        removed = adjusted.removed;
      }

      await refreshCartSurfaces({ keepOpen: true });
      const message = addedBundle
        ? bundleAdjustmentMessage(addedBundle, removed)
        : 'Création ajoutée au panier.';
      announce(message);
      showToast(message);
    } catch (error) {
      announce(error.message || 'Une erreur est survenue.');
      showToast(error.message || 'Une erreur est survenue.', 'warning');
    } finally {
      requestInFlight = false;
      setBusy(submitter, false);
    }
  }

  async function removeLine(key, trigger) {
    if (!key || requestInFlight) return;
    requestInFlight = true;
    setBusy(trigger, true);

    try {
      const response = await fetch(route('/cart/change.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.description || 'Cette création ne peut pas être retirée.');

      await refreshCartSurfaces({ keepOpen: shell()?.classList.contains('is-open') });
      announce('Création retirée du panier.');
    } catch (error) {
      announce(error.message || 'Une erreur est survenue.');
      showToast(error.message || 'Une erreur est survenue.', 'warning');
    } finally {
      requestInFlight = false;
      setBusy(trigger, false);
    }
  }

  /* -------------------------------------------------------------------------
     Téléchargement des créations déjà commandées (FileFlare).
     Même API storefront que Mon inventaire : le jeton de statut de commande
     (non devinable, rendu uniquement pour le client connecté) est échangé
     contre les liens sécurisés des fichiers. En cas d'échec (plan FileFlare,
     réseau, ligne introuvable, possession via Bundle), repli vers Mon
     inventaire, ancré sur la commande concernée.
     ------------------------------------------------------------------------- */
  const FILEFLARE_API = '/apps/digital-downloads/legacy/orders/';
  const OWNED_DOWNLOAD_ICON = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12M7 10l5 5 5-5M5 20h14"></path></svg>';
  const orderFilesCache = new Map();

  function firstDefined(...values) {
    for (const value of values) {
      if (value !== undefined && value !== null && value !== '') return value;
    }
    return null;
  }

  /* Les noms de fichiers viennent d'une API externe : toujours textContent, jamais innerHTML. */
  function normalizeDownloadAsset(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const data = raw.data && typeof raw.data === 'object' ? raw.data : raw;
    const url = firstDefined(data.url, data.download_url, data.downloadUrl, data.link, data.href);
    if (!url || !/^https:\/\//.test(String(url))) return null;
    return {
      name: String(firstDefined(data.name, data.filename, data.file_name, data.title, 'Fichier')),
      url: String(url)
    };
  }

  function downloadAssetsOf(container) {
    if (!container || typeof container !== 'object') return [];
    const arr = firstDefined(container.assets, container.files, container.downloads, container.items);
    return Array.isArray(arr) ? arr : [];
  }

  function lineMatchesRecord(line, record) {
    if (!line || typeof line !== 'object') return false;
    const prod = line.product && typeof line.product === 'object' ? line.product : line;
    const pid = normalizeId(firstDefined(prod.id, line.product_id, line.productId, ''));
    if (pid && record.productId && pid === record.productId) return true;
    const title = String(firstDefined(prod.name, prod.title, line.title, line.name, '')).toLowerCase();
    const wanted = record.title.toLowerCase();
    if (title && wanted && (title === wanted || title.indexOf(wanted) !== -1 || wanted.indexOf(title) !== -1)) return true;
    return false;
  }

  function fetchOrderFiles(token) {
    if (!orderFilesCache.has(token)) {
      const request = fetch(FILEFLARE_API + encodeURIComponent(token), {
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      });
      request.catch(() => orderFilesCache.delete(token));
      orderFilesCache.set(token, request);
    }
    return orderFilesCache.get(token);
  }

  function goToInventory(record) {
    const anchor = record && record.orderId ? `#inv-${record.orderId}` : '';
    window.location.assign((commerceData.inventoryUrl || '/pages/mon-inventaire') + anchor);
  }

  function triggerFileDownloads(assets) {
    assets.forEach((asset, index) => {
      window.setTimeout(() => {
        const anchor = document.createElement('a');
        anchor.href = asset.url;
        anchor.rel = 'nofollow noopener';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }, index * 900);
    });
  }

  async function downloadOwned(productId, handle, trigger) {
    if (trigger && trigger.getAttribute('aria-busy') === 'true') return;
    const record = ownedOrderFor(productId, handle);
    if (!record || !record.direct || !record.token) {
      goToInventory(record);
      return;
    }

    setBusy(trigger, true);
    try {
      const payload = await fetchOrderFiles(record.token);
      const order = payload && payload.order ? payload.order : payload;
      const lines = Array.isArray(order && order.lines) ? order.lines : [];
      const line = lines.find((candidate) => lineMatchesRecord(candidate, record));
      const assets = downloadAssetsOf(line).map(normalizeDownloadAsset).filter(Boolean);
      if (!assets.length) {
        goToInventory(record);
        return;
      }
      triggerFileDownloads(assets);
      const message = assets.length > 1
        ? `Téléchargement lancé : ${assets.length} fichiers PDF.`
        : `Téléchargement lancé : ${assets[0].name}.`;
      announce(message);
      showToast(message);
    } catch (_) {
      goToInventory(record);
    } finally {
      setBusy(trigger, false);
    }
  }

  /* Transforme un bouton d'ajout au panier en bouton de téléchargement. */
  function makeDownloadButton(button, productId, handle, label) {
    if (!button || 'ownedDownload' in button.dataset) return;
    button.dataset.ownedDownload = '';
    button.dataset.ownedProductId = productId ? String(productId) : '';
    button.dataset.ownedHandle = handle || '';
    button.classList.add('is-owned-download');
    button.classList.remove('is-in-cart');
    button.removeAttribute('name');
    /* Sort le bouton des resynchronisations « panier » (sync d'app.js et
       updateCartUi), qui restaureraient data-label-idle (« Ajouter au panier »). */
    button.removeAttribute('data-variant-id');
    button.dataset.labelIdle = label;
    button.dataset.labelDone = label;
    button.setAttribute('aria-label', label);
    const labelEl = button.querySelector('[data-atc-label]');
    if (labelEl) labelEl.textContent = label;
    const idleIcon = button.querySelector('.atc__icon--idle');
    if (idleIcon) idleIcon.innerHTML = OWNED_DOWNLOAD_ICON;
  }

  function createNote(className, text, link = null) {
    const note = document.createElement(link ? 'a' : 'p');
    note.className = className;
    note.textContent = text;
    if (link) note.href = link;
    return note;
  }

  function decorateProductCards() {
    document.querySelectorAll('.product-card').forEach((card) => {
      const link = card.querySelector('.product-card__title-link, .product-card__media');
      const handle = handleFromUrl(link?.href);
      if (!handle) return;
      const productId = productIdByHandle.get(handle);
      const owned = isOwned(productId, handle);
      const memberships = membershipFor(productId, handle);
      const body = card.querySelector('.product-card__body');
      const title = card.querySelector('.product-card__title');

      if (owned) {
        if (!card.querySelector('[data-owned-card-note]')) {
          const note = createNote('product-commerce-note product-commerce-note--owned', 'Déjà commandé avec ce compte');
          note.dataset.ownedCardNote = '';
          title?.insertAdjacentElement('afterend', note);
        }
        card.querySelectorAll('[data-atc]').forEach((button) => {
          makeDownloadButton(button, productId, handle, 'Télécharger');
        });
      }

      if (memberships.length && !card.querySelector('[data-bundle-card-note]')) {
        const first = memberships[0];
        const suffix = memberships.length > 1
          ? ` et ${memberships.length - 1} autre${memberships.length > 2 ? 's' : ''} Pack${memberships.length > 2 ? 's' : ''}`
          : '';
        const note = createNote(
          'product-commerce-note product-commerce-note--bundle',
          `Inclus dans ${cleanTitle(first.title)}${suffix}`,
          first.url
        );
        note.dataset.bundleCardNote = '';
        const anchor = card.querySelector('[data-owned-card-note]') || title;
        anchor?.insertAdjacentElement('afterend', note);
      }

      if (body && (owned || memberships.length)) card.classList.add('has-commerce-signals');
    });
  }

  function createPdpNotice({ tone, title, body, links = [] }) {
    const notice = document.createElement('div');
    notice.className = `commerce-notice commerce-notice--${tone}`;
    const heading = document.createElement('strong');
    heading.className = 'commerce-notice__title';
    heading.textContent = title;
    const copy = document.createElement('p');
    copy.className = 'commerce-notice__body';
    copy.textContent = body;
    notice.append(heading, copy);

    if (links.length) {
      const actions = document.createElement('div');
      actions.className = 'commerce-notice__links';
      links.forEach(({ label, url }) => {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.textContent = label;
        actions.appendChild(anchor);
      });
      notice.appendChild(actions);
    }
    return notice;
  }

  function decoratePdp() {
    const current = commerceData.currentProduct;
    const info = document.querySelector('.pdp__info-col');
    const form = info?.querySelector('.pdp__form');
    if (!current || !info || !form || info.querySelector('[data-commerce-pdp-notices]')) return;

    const productId = normalizeId(current.id);
    const handle = current.handle || handleFromUrl(window.location.href);
    const owned = isOwned(productId, handle);
    const memberships = membershipFor(productId, handle);
    const notices = document.createElement('div');
    notices.className = 'commerce-pdp-notices';
    notices.dataset.commercePdpNotices = '';
    /* Refonte 2027 : les encarts « déjà à toi » et « Pack » sont rendus côté serveur (snippet rf-pdp-extras).
       On garde seulement la transformation du bouton en téléchargement direct. */
    const serverNotices = Boolean(document.querySelector('[data-rf-pdp]'));

    if (owned && !serverNotices) {
      notices.appendChild(createPdpNotice({
        tone: 'owned',
        title: 'Déjà commandé avec ce compte',
        body: 'Cette création figure dans tes commandes. Télécharge-la directement, elle est déjà à toi.',
        links: [{ label: 'Ouvrir mon inventaire', url: commerceData.inventoryUrl || '/pages/mon-inventaire' }]
      }));
    }
    if (owned) {
      info.querySelectorAll('[data-atc]').forEach((button) => {
        makeDownloadButton(button, productId, handle, 'Télécharger le PDF');
      });
      document.querySelectorAll('.pdp-sticky [data-atc]').forEach((button) => {
        makeDownloadButton(button, productId, handle, 'Télécharger');
      });
      /* La quantité n'a plus de sens : le bouton télécharge, il n'ajoute plus. */
      info.querySelectorAll('.pdp__qty').forEach((qty) => { qty.style.display = 'none'; });
    }

    if (memberships.length && !serverNotices) {
      const visible = memberships.slice(0, 3);
      const links = visible.map((bundle) => ({ label: cleanTitle(bundle.title), url: bundle.url }));
      const body = memberships.length === 1
        ? `Cette création est également comprise dans ${cleanTitle(memberships[0].title)}, avec ${Math.max(0, memberships[0].components.length - 1)} autres contenus.`
        : `Cette création est comprise dans ${memberships.length} Packs. Compare-les avant de commander à l’unité.`;
      notices.appendChild(createPdpNotice({
        tone: 'bundle',
        title: memberships.length === 1 ? `Disponible dans ${cleanTitle(memberships[0].title)}` : 'Disponible dans plusieurs Packs',
        body,
        links
      }));
    }

    if (!commerceData.customerLoggedIn && !serverNotices) {
      notices.appendChild(createPdpNotice({
        tone: 'account',
        title: 'Déjà client ?',
        body: 'Connecte-toi pour vérifier si cette création figure déjà dans tes commandes.',
        links: [{ label: 'Vérifier mes achats', url: commerceData.accountUrl || '/account' }]
      }));
    }

    if (notices.children.length) form.insertAdjacentElement('beforebegin', notices);
  }

  function decorateCartLines() {
    document.querySelectorAll('.cart-drawer__line').forEach((line) => {
      const link = line.querySelector('.cart-drawer__line-title, .cart-drawer__line-media');
      const handle = handleFromUrl(link?.href);
      if (!handle) return;
      const productId = productIdByHandle.get(handle);
      const info = line.querySelector('.cart-drawer__line-info');
      const owned = isOwned(productId, handle);
      const memberships = membershipFor(productId, handle);

      if (owned && !line.querySelector('[data-owned-cart-line]')) {
        const note = createNote('cart-drawer__line-signal cart-drawer__line-signal--owned', 'Déjà commandé avec ce compte');
        note.dataset.ownedCartLine = '';
        info?.appendChild(note);
      }

      if (memberships.length && !line.querySelector('[data-bundle-cart-line]')) {
        const first = memberships[0];
        const note = createNote(
          'cart-drawer__line-signal cart-drawer__line-signal--bundle',
          `Existe aussi dans ${cleanTitle(first.title)}`,
          first.url
        );
        note.dataset.bundleCartLine = '';
        info?.appendChild(note);
      }
    });
  }

  function ensureAdviceContainers() {
    const drawerBody = document.querySelector('[data-cart-drawer] .cart-drawer__body');
    if (drawerBody && !drawerBody.querySelector('[data-bundle-cart-advice="drawer"]')) {
      const container = document.createElement('div');
      container.className = 'bundle-cart-advice bundle-cart-advice--drawer';
      container.dataset.bundleCartAdvice = 'drawer';
      const anchor = drawerBody.querySelector('.cart-recommendation, .cart-drawer__digital-note');
      if (anchor) anchor.insertAdjacentElement('beforebegin', container);
      else drawerBody.appendChild(container);
    }

    const cartNudges = document.querySelector('[data-cart-nudges]');
    if (cartNudges && !cartNudges.querySelector('[data-bundle-cart-advice="cart"]')) {
      const container = document.createElement('div');
      container.className = 'bundle-cart-advice bundle-cart-advice--cart';
      container.dataset.bundleCartAdvice = 'cart';
      const inner = cartNudges.querySelector('.cart-nudges__inner');
      if (inner) inner.insertAdjacentElement('beforebegin', container);
      else cartNudges.appendChild(container);
    }
  }

  function findBestBundleUpgrade(cart) {
    const cartProductIds = new Set(cart.items.map((item) => normalizeId(item.product_id)));
    const units = cart.items.filter((item) => !isBundleLine(item));
    const candidates = [];

    bundles.forEach((bundle) => {
      if (cartProductIds.has(bundle.id) || ownedProductIds.has(bundle.id)) return;

      const matching = units.filter((item) => bundle.componentIds.has(normalizeId(item.product_id)));
      if (matching.length < 2) return;

      const matchingSubtotal = matching.reduce(
        (total, item) => total + Number(item.final_line_price ?? item.line_price ?? 0),
        0
      );
      const matchingIds = new Set(matching.map((item) => normalizeId(item.product_id)));
      const ownedOverlap = bundle.components.filter((component) => ownedProductIds.has(component.id));
      const additional = bundle.components.filter(
        (component) => !matchingIds.has(component.id) && !ownedProductIds.has(component.id)
      );
      const difference = bundle.price - matchingSubtotal;
      const costPerAdditional = difference > 0 && additional.length
        ? difference / additional.length
        : 0;

      if (difference > 0 && (!additional.length || costPerAdditional > 500)) return;

      const score = (matching.length * 100000)
        + (difference <= 0 ? 50000 + Math.abs(difference) : 0)
        + (additional.length * 1000)
        - Math.max(0, difference);

      candidates.push({
        bundle,
        matching,
        matchingSubtotal,
        difference,
        additionalCount: additional.length,
        ownedOverlapCount: ownedOverlap.length,
        score
      });
    });

    return candidates.sort((a, b) => b.score - a.score)[0] || null;
  }

  function createOwnedCartWarning(cart) {
    if (!commerceData.customerLoggedIn) return null;
    const alreadyOrdered = cart.items.filter((item) => ownedProductIds.has(normalizeId(item.product_id)));
    if (!alreadyOrdered.length) return null;

    const card = document.createElement('div');
    card.className = 'bundle-advice-card bundle-advice-card--owned';
    const title = document.createElement('strong');
    title.className = 'bundle-advice-card__title';
    title.textContent = 'Vérifie avant de repayer';
    const body = document.createElement('p');
    body.className = 'bundle-advice-card__body';
    body.textContent = alreadyOrdered.length === 1
      ? 'Ce compte a déjà commandé une création présente dans ce panier.'
      : `Ce compte a déjà commandé ${alreadyOrdered.length} créations présentes dans ce panier.`;
    const link = document.createElement('a');
    link.className = 'bundle-advice-card__link';
    link.href = commerceData.inventoryUrl || '/pages/mon-inventaire';
    link.textContent = 'Consulter mon inventaire';
    card.append(title, body, link);
    return card;
  }

  function createBundleUpgradeCard(upgrade) {
    if (!upgrade) return null;
    const { bundle, matching, difference, additionalCount, ownedOverlapCount } = upgrade;
    const card = document.createElement('div');
    card.className = 'bundle-advice-card bundle-advice-card--upgrade';

    const eyebrow = document.createElement('span');
    eyebrow.className = 'bundle-advice-card__eyebrow';
    eyebrow.textContent = 'Une offre plus cohérente pour ce panier';

    const title = document.createElement('strong');
    title.className = 'bundle-advice-card__title';
    title.textContent = `Ton panier contient ${matching.length} créations du ${cleanTitle(bundle.title)}`;

    const body = document.createElement('p');
    body.className = 'bundle-advice-card__body';
    if (difference < 0) {
      body.textContent = `Passe au Pack : ${bundle.components.length} créations au total et ${money(Math.abs(difference))} de moins.`;
    } else if (difference === 0) {
      body.textContent = `Au même prix, le Pack ajoute ${additionalCount} création${additionalCount > 1 ? 's' : ''} supplémentaire${additionalCount > 1 ? 's' : ''}.`;
    } else {
      body.textContent = `Pour ${money(difference)} de plus, le Pack ajoute ${additionalCount} autre${additionalCount > 1 ? 's' : ''} création${additionalCount > 1 ? 's' : ''}.`;
    }

    card.append(eyebrow, title, body);

    if (ownedOverlapCount > 0) {
      const ownership = document.createElement('p');
      ownership.className = 'bundle-advice-card__fineprint';
      ownership.textContent = `Ton compte a déjà commandé ${ownedOverlapCount} création${ownedOverlapCount > 1 ? 's' : ''} de ce Pack ; ${ownedOverlapCount > 1 ? 'elles ne sont' : 'elle n’est'} pas comptée${ownedOverlapCount > 1 ? 's' : ''} comme un gain supplémentaire.`;
      card.appendChild(ownership);
    }

    const actions = document.createElement('div');
    actions.className = 'bundle-advice-card__actions';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn--primary btn--sm';
    button.dataset.bundleUpgrade = String(bundle.variantId);
    button.textContent = `Choisir ${cleanTitle(bundle.title)}`;
    const link = document.createElement('a');
    link.className = 'bundle-advice-card__link';
    link.href = bundle.url;
    link.textContent = 'Voir le détail du Pack';
    actions.append(button, link);
    card.appendChild(actions);
    return card;
  }

  function renderCartAdvice(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    ensureAdviceContainers();
    const upgrade = findBestBundleUpgrade(cart);

    document.querySelectorAll('[data-bundle-cart-advice]').forEach((container) => {
      container.replaceChildren();
      const ownedWarning = createOwnedCartWarning(cart);
      const upgradeCard = createBundleUpgradeCard(upgrade);
      if (ownedWarning) container.appendChild(ownedWarning);
      if (upgradeCard) container.appendChild(upgradeCard);
      container.hidden = !container.children.length;
    });
  }

  function decorateCommerce(cart = null) {
    decorateProductCards();
    decoratePdp();
    decorateCartLines();
    if (cart) renderCartAdvice(cart);
  }

  async function replaceWithBundle(variantId, trigger) {
    if (requestInFlight) return;
    const bundle = bundleByVariant.get(normalizeId(variantId));
    if (!bundle) return;

    requestInFlight = true;
    setBusy(trigger, true);

    try {
      const currentCart = await fetchCart();
      if (!currentCart.items.some((item) => normalizeId(item.variant_id) === bundle.variantId)) {
        await addVariant(bundle.variantId);
      }
      const cartAfterAdd = await fetchCart();
      const adjusted = await removeCoveredUnits(cartAfterAdd);
      await refreshCartSurfaces({ keepOpen: shell()?.classList.contains('is-open') });

      const count = adjusted.removed.reduce((total, item) => total + item.quantity, 0);
      const message = count
        ? `Panier optimisé : ${cleanTitle(bundle.title)} remplace ${count} création${count > 1 ? 's' : ''} unitaire${count > 1 ? 's' : ''}.`
        : `${cleanTitle(bundle.title)} est maintenant dans ton panier.`;
      announce(message);
      showToast(message);
    } catch (error) {
      announce(error.message || 'Une erreur est survenue.');
      showToast(error.message || 'Une erreur est survenue.', 'warning');
    } finally {
      requestInFlight = false;
      setBusy(trigger, false);
    }
  }

  function applyPromo(input) {
    const code = input?.value.trim();
    if (!code) {
      input?.focus();
      return;
    }
    window.location.assign(`${route('/discount/')}${encodeURIComponent(code)}?redirect=${encodeURIComponent('/cart')}`);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-cart-add], form[action*="/cart/add"]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (submitter && 'ownedDownload' in submitter.dataset) {
      downloadOwned(submitter.dataset.ownedProductId, submitter.dataset.ownedHandle, submitter);
      return;
    }
    addToCart(form, submitter);
  }, true);

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('.header__cart');
    if (cartLink && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
      event.preventDefault();
      openDrawer();
      return;
    }

    if (event.target.closest('[data-cart-drawer-close], [data-cart-drawer-backdrop]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const upgrade = event.target.closest('[data-bundle-upgrade]');
    if (upgrade) {
      event.preventDefault();
      replaceWithBundle(upgrade.dataset.bundleUpgrade, upgrade);
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      event.preventDefault();
      removeLine(remove.dataset.key, remove);
      return;
    }

    const promoButton = event.target.closest('[data-promo-apply]');
    if (promoButton) {
      event.preventDefault();
      applyPromo(promoButton.closest('.cart-commerce__promo')?.querySelector('[data-promo-input]'));
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && shell()?.classList.contains('is-open')) closeDrawer();
    if (event.key === 'Enter' && event.target.matches('[data-promo-input]')) {
      event.preventDefault();
      applyPromo(event.target);
    }
  });

  decorateCommerce();

  window.addEventListener('pageshow', async () => {
    try {
      const cart = await fetchCart();
      const adjusted = await removeCoveredUnits(cart);
      if (adjusted.removed.length) {
        await refreshCartSurfaces();
        const count = adjusted.removed.reduce((total, item) => total + item.quantity, 0);
        const message = `Panier ajusté : ${count} création${count > 1 ? 's' : ''} unitaire${count > 1 ? 's' : ''} déjà incluse${count > 1 ? 's' : ''} dans tes Packs ${count > 1 ? 'ont été retirées' : 'a été retirée'}.`;
        announce(message);
        showToast(message);
      } else {
        updateCartUi(cart);
        decorateCommerce(cart);
      }
    } catch (_) {
      decorateCommerce();
    }
  });
})();
